/* eslint-disable no-loop-func, no-plusplus, no-await-in-loop, consistent-return, arrow-body-style, no-promise-executor-return */

"use client"

import gsap from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import React, {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react"

import DesktopSVG from "../../../../../public/animations/swell-hero-desktop.svg"
import MobileSVG from "../../../../../public/animations/swell-hero-mobile.svg"

gsap.registerPlugin(ScrollTrigger)

// Throttle utility for performance
// eslint-disable-next-line func-names
const throttle = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout | null = null
  let lastExecTime = 0
  // eslint-disable-next-line func-names
  return function (this: any, ...args: any[]) {
    const currentTime = Date.now()

    if (currentTime - lastExecTime > delay) {
      func.apply(this, args)
      lastExecTime = currentTime
    } else {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(
        // eslint-disable-next-line func-names
        function () {
          func.apply(this, args)
          lastExecTime = Date.now()
        },
        delay - (currentTime - lastExecTime),
      )
    }
  }
}

interface AnimationConfig {
  frameBasePath: string
  frameCount: number
  canvasWidth: number
  canvasHeight: number
  clipPathInsetHeight: number
  clipStartOffset: number
  clipEndOffset: number
  midpointTriggerOffset: number
  endTriggerOffset: number
  threeQuarterTriggerOffset: number
  containerMinHeight: string
}

export default function ScrollAnimationCanvas() {
  // === Refs for DOM elements ===
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const textBlockRef = useRef<HTMLDivElement>(null)
  const midpointRef = useRef<HTMLDivElement>(null)
  const threeQRef = useRef<HTMLDivElement>(null)
  const imagesRef = useRef<HTMLImageElement[]>([])
  const frameIndex = useRef(0)
  const requestId = useRef<number | null>(null)
  const scrollTriggersRef = useRef<gsap.core.Tween[]>([])
  const isAnimatingRef = useRef(false)

  // === State for container height ===
  const [containerHeight, setContainerHeight] = useState(0)

  // === Progressive image loading function (background loading) ===
  const loadImagesProgressively = useCallback(
    async (config: AnimationConfig) => {
      const BATCH_SIZE = 8 // Load 8 images at a time for better performance
      const images: HTMLImageElement[] = new Array(config.frameCount)

      try {
        // Helper to get correct frame image src
        const getFrameSrc = (i: number) =>
          `${config.frameBasePath}${String(i).padStart(4, "0")}.png`

        // Load first image immediately for initial display
        const firstImg = new Image()
        firstImg.src = getFrameSrc(0)
        await new Promise((resolve, reject) => {
          firstImg.onload = resolve
          firstImg.onerror = reject
        })
        images[0] = firstImg
        imagesRef.current = images

        // Draw first frame immediately
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext("2d")
          if (ctx) {
            ctx.clearRect(
              0,
              0,
              canvasRef.current.width,
              canvasRef.current.height,
            )
            ctx.drawImage(
              firstImg,
              0,
              0,
              canvasRef.current.width,
              canvasRef.current.height,
            )
          }
        }

        // Load remaining images in batches (background loading)
        const loadBatch = async (startIndex: number, endIndex: number) => {
          const batch = []
          for (let j = startIndex; j < endIndex; j += 1) {
            const img = new Image()
            img.src = getFrameSrc(j)
            batch.push(
              new Promise<void>((resolve) => {
                const handleLoad = () => {
                  images[j] = img
                  resolve()
                }
                img.onload = handleLoad
                img.onerror = () => resolve() // Continue even if some images fail
              }),
            )
          }
          return Promise.all(batch)
        }

        // Process all batches in background
        const batchPromises = []
        for (let i = 1; i < config.frameCount; i += BATCH_SIZE) {
          const batchEnd = Math.min(i + BATCH_SIZE, config.frameCount)
          batchPromises.push(loadBatch(i, batchEnd))
        }

        // Wait for all batches to complete
        await Promise.all(batchPromises)
        imagesRef.current = images
      } catch (error) {
        console.warn("Some animation images failed to load:", error)
        // Continue with available images
      }
    },
    [],
  )

  // === Optimized canvas drawing function ===
  const drawFrame = useCallback((idx: number) => {
    const canvas = canvasRef.current
    const img = imagesRef.current[idx]

    if (!canvas || !img || !img.complete) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Use requestAnimationFrame for smoother rendering
    if (requestId.current) {
      cancelAnimationFrame(requestId.current)
    }

    requestId.current = requestAnimationFrame(() => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      isAnimatingRef.current = false
    })
  }, [])

  // === Throttled scroll handler for smooth performance ===
  const onScroll = useMemo(
    () =>
      throttle(() => {
        if (!containerRef.current || isAnimatingRef.current) return

        isAnimatingRef.current = true
        const top = window.scrollY
        const max = containerRef.current.offsetHeight - window.innerHeight
        const frac = Math.min(Math.max(top / max, 0), 1)
        const newFrameIndex = Math.floor(frac * (imagesRef.current.length - 1))

        if (
          newFrameIndex !== frameIndex.current &&
          imagesRef.current[newFrameIndex]
        ) {
          frameIndex.current = newFrameIndex
          drawFrame(frameIndex.current)
        } else {
          isAnimatingRef.current = false
        }
      }, 16),
    [drawFrame],
  ) // 60fps throttling for smooth scrolling

  // === Container height watcher ===
  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const handleResize = throttle(() => {
      setContainerHeight(node.clientHeight)
    }, 100)

    window.addEventListener("resize", handleResize)
    const ro = new ResizeObserver(handleResize)
    ro.observe(node)
    handleResize()

    return () => {
      window.removeEventListener("resize", handleResize)
      ro.disconnect()
    }
  }, [])

  // === Main animation setup ===
  useLayoutEffect(() => {
    if (!containerRef.current) return

    const mm = gsap.matchMedia()

    mm.add(
      {
        // You can use more breakpoints if needed!
        isMobile: "(max-width: 624px)",
        isDesktop: "(min-width: 625px)",
      },
      (context) => {
        // Configuration based on breakpoint
        const config: AnimationConfig = context.conditions.isMobile
          ? {
              frameBasePath: "/animations/swell-25-mobile/SwellWebsiteMobile",
              frameCount: 121,
              canvasWidth: 375,
              canvasHeight: 667,
              clipPathInsetHeight: 64,
              clipStartOffset: 160,
              clipEndOffset: -80,
              midpointTriggerOffset: 194,
              endTriggerOffset: 127,
              threeQuarterTriggerOffset: 180,
              containerMinHeight: "md:min-h-[455px]",
            }
          : {
              frameBasePath: "/animations/swell-25-desktop/SwellWebsiteDesktop",
              frameCount: 121,
              canvasWidth: 1280,
              canvasHeight: 717,
              clipPathInsetHeight: 163,
              clipStartOffset: 160,
              clipEndOffset: -172,
              midpointTriggerOffset: 241.5,
              endTriggerOffset: 81.5,
              threeQuarterTriggerOffset: 260,
              containerMinHeight: "md:min-h-[800px]",
            }

        // Update canvas size for optimal performance
        if (canvasRef.current) {
          canvasRef.current.width = config.canvasWidth
          canvasRef.current.height = config.canvasHeight
        }

        // Load images progressively in background
        loadImagesProgressively(config)

        // Add optimized scroll listener with passive flag
        window.addEventListener("scroll", onScroll, { passive: true })

        // Cleanup function for memory management
        const killAll = () => {
          scrollTriggersRef.current.forEach((trigger) => trigger.kill())
          ScrollTrigger.getAll().forEach((st) => st.kill())
          window.removeEventListener("scroll", onScroll)

          // Clear canvas and images for garbage collection
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext("2d")
            if (ctx) {
              ctx.clearRect(
                0,
                0,
                canvasRef.current.width,
                canvasRef.current.height,
              )
            }
          }

          // Clear image references
          imagesRef.current = []

          if (requestId.current) {
            cancelAnimationFrame(requestId.current)
            requestId.current = null
          }
        }

        // Set up GSAP ScrollTriggers for smooth animations
        const setupScrollTriggers = () => {
          // White overlay wipe animation
          const overlayTimeline = gsap
            .timeline({
              scrollTrigger: {
                trigger: midpointRef.current,
                start: `bottom-=${config.midpointTriggerOffset} center`,
                end: `bottom-=${config.endTriggerOffset} center`,
                scrub: true,
              },
            })
            .fromTo(
              overlayRef.current,
              { clipPath: "inset(0 0 0px 0)" },
              {
                clipPath: `inset(0 0 ${config.clipPathInsetHeight}px 0)`,
                ease: "none",
              },
            )
          scrollTriggersRef.current.push(
            overlayTimeline as unknown as gsap.core.Tween,
          )

          // Optimized text movement with GPU acceleration
          if (textBlockRef.current) {
            textBlockRef.current.style.willChange = "transform, position"
          }

          const textScrollTrigger = ScrollTrigger.create({
            trigger: containerRef.current,
            start: "top+=180 center",
            endTrigger: threeQRef.current,
            end: `top-=${config.threeQuarterTriggerOffset} center`,
            scrub: true,
            onUpdate(self) {
              if (textBlockRef.current) {
                const isFixed = self.progress < 1
                textBlockRef.current.style.position = isFixed
                  ? "fixed"
                  : "absolute"
                textBlockRef.current.style.transform = isFixed
                  ? `translateY(${config.clipStartOffset}px)`
                  : `translateY(${config.clipEndOffset}px)`
              }
            },
          })
          scrollTriggersRef.current.push(
            textScrollTrigger as unknown as gsap.core.Tween,
          )
        }

        // Setup ScrollTriggers immediately
        setupScrollTriggers()

        return killAll
      },
    )

    return () => mm.revert()
  }, [containerHeight, loadImagesProgressively, onScroll])

  // === Cleanup on unmount ===
  useEffect(() => {
    return () => {
      if (requestId.current) {
        cancelAnimationFrame(requestId.current)
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="relative h-[200vh] md:min-h-[992px] overflow-hidden"
      style={{ background: "linear-gradient(to bottom,#001C5C 60%,white 40%)" }}
    >
      {/* Canvas with GPU acceleration for smooth performance */}
      <canvas
        ref={canvasRef}
        width={1280}
        height={992}
        className="absolute inset-0 w-full h-[120vh] z-20 pointer-events-none"
        style={{
          willChange: "transform",
          transform: "translateZ(0)", // Force GPU layer for performance
        }}
      />

      <div ref={midpointRef} className="absolute top-[60%] w-full h-0" />

      <div
        ref={threeQRef}
        className="absolute top-[82.5%] w-full h-0 pointer-events-none text-newblue-90"
      >
        <div className="relative">
          <div
            ref={textBlockRef}
            className="fixed inset-0 flex items-center justify-center z-10 uppercase"
            style={{
              willChange: "transform, position",
              transform: "translateZ(0)", // GPU acceleration for smooth text movement
            }}
          >
            <div className="relative">
              <MobileSVG
                className="block md:hidden"
                color="#001C5C"
                fill="#001C5C"
              />
              <DesktopSVG
                className="hidden md:block"
                color="#001C5C"
                fill="#001C5C"
              />
              <div
                ref={overlayRef}
                className="absolute inset-0"
                style={{
                  clipPath: "inset(0 0 0% 0)",
                  willChange: "clip-path", // Optimize clip-path animations
                }}
              >
                <MobileSVG
                  className="block md:hidden"
                  color="#FFFFFF"
                  fill="#FFFFFF"
                />
                <DesktopSVG
                  className="hidden md:block"
                  color="#FFFFFF"
                  fill="#FFFFFF"
                />
              </div>
            </div>
          </div>
          <p className="text-center body2 mt-10 max-w-[784px] md:text-[26px] mx-auto px-4 leading-snug">
            Join us for two days of unmatched networking and fresh insights from
            global leaders in the industry.
          </p>
        </div>
      </div>
    </div>
  )
}
